/**
 * Public portal layout.
 *
 * ===========================================================================
 * THIS SUBTREE LOADS NO FIREBASE AND NO ANALYTICS
 * ===========================================================================
 * A citizen reading the landing page or filing a report must not download the
 * Firebase SDK, must not initialise a Firebase app, and must not be measured.
 * That is not a performance preference — the RDTB page carries an anonymity
 * promise, and every third-party request made from it carries the visitor's IP
 * to someone else.
 *
 * Nothing under features/public may import from features/officer, lib/firebase,
 * or any analytics package. The officer application lives behind a lazy route
 * boundary precisely so that this stays true by construction rather than by
 * discipline.
 */

import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../app/ThemeProvider';

export function PublicLayout() {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useTheme();

  return (
    <div className="flex min-h-screen flex-col">
      <header
        className="border-b"
        style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-3">
          <Link to="/" className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              {t('app.department')} · {t('app.government')}
            </p>
            <p className="truncate text-base font-semibold">{t('app.tagline')}</p>
          </Link>

          <nav className="flex items-center gap-1" aria-label={t('public.nav')}>
            <PublicLink to="/" end>
              {t('public.home')}
            </PublicLink>
            <PublicLink to="/report">{t('public.report')}</PublicLink>
            <PublicLink to="/track">{t('public.track')}</PublicLink>
          </nav>

          <button
            type="button"
            onClick={toggleLanguage}
            className="rounded-md px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)',
            }}
            lang={language === 'en' ? 'ur' : 'en'}
          >
            {t('language.switchTo')}
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8">
        <Outlet />
      </main>

      <footer
        className="border-t px-4 py-6 text-sm"
        style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <p>
            {t('app.department')} · {t('app.government')}
          </p>
          {/* A plain link, not an auth-aware control — rendering one would mean
              loading the auth context on the public page. /sign-in redirects an
              already-signed-in officer onward by itself. */}
          <Link to="/sign-in" style={{ color: 'var(--accent)' }}>
            {t('public.officerSignIn')}
          </Link>
        </div>
      </footer>
    </div>
  );
}

function PublicLink({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className="rounded-md px-3 py-2 text-sm font-medium"
      style={({ isActive }) => ({
        background: isActive ? 'var(--accent)' : 'transparent',
        color: isActive ? '#fff' : 'var(--text-secondary)',
      })}
    >
      {children}
    </NavLink>
  );
}
